import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { account } from '../api/client';
import AuthLayout from '../components/AuthLayout';
import PasswordField from '../components/PasswordField';

/**
 * Shown straight after signing in with an admin-issued temporary password.
 * Not dismissible - the server refuses every other ERM call until this is done,
 * so there is nothing behind it to go back to.
 *
 * The temporary password typed at sign-in is passed in (held in memory only) so
 * the employee is not asked for it a second time. After a page reload it is
 * gone, and the screen asks for it instead.
 */
export default function ForcedPasswordChange({ me, currentPassword, onDone, onSignOut }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const known = !!currentPassword;
  const temp = known ? currentPassword : current;
  const rules = [
    { ok: next.length >= 8, text: 'At least 8 characters' },
    { ok: next.length > 0 && next !== temp, text: 'Different from the temporary password' },
    { ok: next.length > 0 && next === confirm, text: 'Both entries match' },
  ];
  const valid = rules.every((r) => r.ok) && (known || current.length > 0);

  async function submit(e) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await account.changePassword(temp, next);
      onDone();
    } catch (err) {
      setError(err.status === 403 ? 'The temporary password is not correct.' : err.message);
    } finally {
      setBusy(false);
    }
  }

  const subtitle = `${me.name ? `${known ? 'Welcome' : 'Hi'}, ${me.name.split(' ')[0]}. ` : ''}${
    known
      ? 'You signed in with a temporary password. Set your own to continue.'
      : 'Your password was reset by HR. Enter the temporary password you were given, then choose your own.'}`;

  return (
    <AuthLayout title="Choose your password" subtitle={subtitle} onSubmit={submit}>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {!known && (
          <PasswordField label="Temporary password" value={current} required autoFocus
                         autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} />
        )}
        <PasswordField label="New password" value={next} required autoFocus={known}
                       autoComplete="new-password" onChange={(e) => setNext(e.target.value)} />
        <PasswordField label="Confirm new password" value={confirm} required
                       autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {rules.map((r) => (
            <Typography component="li" key={r.text} variant="body2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25,
                              color: r.ok ? 'success.main' : 'text.secondary' }}>
              {r.ok ? <CheckCircle sx={{ fontSize: 17 }} /> : <RadioButtonUnchecked sx={{ fontSize: 17 }} />}
              {r.text}
            </Typography>
          ))}
        </Box>

        <Button type="submit" variant="contained" size="large" fullWidth disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Set password and continue'}
        </Button>
        <Button onClick={onSignOut} color="inherit" size="small" sx={{ color: 'text.secondary' }}>
          Sign out instead
        </Button>
      </Stack>
    </AuthLayout>
  );
}
